import React, { useState } from 'react';
import { Lock, ShieldAlert, ShieldX, CheckCircle2, ScanFace, CreditCard, IdCard, Loader2, Smartphone } from 'lucide-react';
import { useSafety, FINES } from './SafetyContext';

function FineTable({ current }) {
  const rows = [
    { n: '1st', amt: '₹300' }, { n: '2nd', amt: '₹500' },
    { n: '3rd', amt: '₹1,000' }, { n: '4th', amt: '₹2,000' },
    { n: '5th', amt: 'Account disabled' },
  ];
  return (
    <div className="rounded-xl overflow-hidden ring-1 ring-[#1c2634] mt-4">
      {rows.map((r, i) => (
        <div key={r.n} className={`flex items-center justify-between px-3.5 py-2 text-[13px] ${current === i + 1 ? 'bg-[#12233a]' : 'bg-[#0c1420]'}`}>
          <span className={current === i + 1 ? 'text-white font-semibold' : 'text-[#8b95a5]'}>{r.n} violation</span>
          <span className={`${r.amt.includes('disabled') ? 'text-[#f43f5e]' : current === i + 1 ? 'text-[#f5a524] font-bold' : 'text-[#c4ccd8]'}`}>{r.amt}</span>
        </div>
      ))}
    </div>
  );
}

export function LockScreen() {
  const { violations, currentFine, unlock, lastReason, resetDemo } = useSafety();
  const [paying, setPaying] = useState(false);
  const [paid, setPaid] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [selfieOk, setSelfieOk] = useState(false);
  const [idNo, setIdNo] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');

  const idOk = idNo.trim().length >= 6;
  const otpOk = otp.length === 6;
  const canUnlock = paid && selfieOk && idOk && otpOk;

  const pay = () => { setPaying(true); setTimeout(() => { setPaying(false); setPaid(true); }, 1400); };
  const scan = () => { setScanning(true); setTimeout(() => { setScanning(false); setSelfieOk(true); }, 1800); };

  return (
    <div className="absolute inset-0 z-50 bg-[#05070c]/95 backdrop-blur-sm overflow-y-auto app-scroll">
      <div className="min-h-full px-6 py-8 flex flex-col">
        <div className="w-16 h-16 rounded-2xl bg-[#3a2a12] flex items-center justify-center mx-auto mb-4">
          <Lock size={30} className="text-[#f5a524]" />
        </div>
        <h2 className="font-display text-xl font-bold text-white text-center">Account Locked</h2>
        <p className="text-[13px] text-[#8b95a5] text-center mt-1.5 leading-snug">
          Violation #{violations} detected: <span className="text-[#f5a524]">{lastReason}</span>.
          Your account is locked until you pay the fine and verify your identity.
        </p>

        <FineTable current={violations} />

        <div className="mt-5 space-y-3">
          <div className={`rounded-xl p-3.5 ring-1 ${paid ? 'ring-[#22c55e]/40 bg-[#0c1c14]' : 'ring-[#1c2634] bg-[#0c1420]'}`}>
            <div className="flex items-center gap-2 mb-2">
              {paid ? <CheckCircle2 size={18} className="text-[#22c55e]" /> : <CreditCard size={18} className="text-[#2f8bff]" />}
              <span className="text-[14px] font-semibold text-white">Step 1 · Pay fine</span>
              <span className="ml-auto text-[15px] font-bold text-[#f5a524]">₹{currentFine}</span>
            </div>
            {paid ? (
              <p className="text-[12px] text-[#22c55e]">Payment received (mock).</p>
            ) : (
              <button onClick={pay} disabled={paying} className="w-full py-2.5 rounded-lg bg-[#2f8bff] text-white text-[14px] font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
                {paying ? <><Loader2 size={16} className="animate-spin" /> Processing...</> : `Pay ₹${currentFine}`}
              </button>
            )}
          </div>

          <div className={`rounded-xl p-3.5 ring-1 ${selfieOk && idOk ? 'ring-[#22c55e]/40 bg-[#0c1c14]' : 'ring-[#1c2634] bg-[#0c1420]'}`}>
            <div className="flex items-center gap-2 mb-2">
              {selfieOk && idOk ? <CheckCircle2 size={18} className="text-[#22c55e]" /> : <IdCard size={18} className="text-[#2f8bff]" />}
              <span className="text-[14px] font-semibold text-white">Step 2 · Verify identity</span>
            </div>
            <input
              value={idNo}
              onChange={(e) => setIdNo(e.target.value)}
              placeholder="Government ID number"
              className="w-full bg-[#0a0f18] rounded-lg px-3 py-2.5 text-[13px] text-white placeholder:text-[#5c6675] outline-none ring-1 ring-[#1c2634] focus:ring-[#2f8bff] mb-2.5"
            />
            {selfieOk ? (
              <p className="flex items-center gap-1.5 text-[12px] text-[#22c55e]"><ScanFace size={15} /> Live selfie verified (mock).</p>
            ) : (
              <button onClick={scan} disabled={scanning} className="w-full py-2.5 rounded-lg bg-[#161d29] text-white text-[14px] font-semibold flex items-center justify-center gap-2 ring-1 ring-[#1c2634] active:scale-[0.98] transition-transform">
                {scanning ? <><Loader2 size={16} className="animate-spin" /> Scanning face...</> : <><ScanFace size={16} /> Start live identification</>}
              </button>
            )}
          </div>

          <div className={`rounded-xl p-3.5 ring-1 ${otpOk ? 'ring-[#22c55e]/40 bg-[#0c1c14]' : 'ring-[#1c2634] bg-[#0c1420]'}`}>
            <div className="flex items-center gap-2 mb-2">
              {otpOk ? <CheckCircle2 size={18} className="text-[#22c55e]" /> : <Smartphone size={18} className="text-[#2f8bff]" />}
              <span className="text-[14px] font-semibold text-white">Step 3 · Two-factor authentication</span>
            </div>
            {!otpSent ? (
              <button onClick={() => setOtpSent(true)} className="w-full py-2.5 rounded-lg bg-[#161d29] text-white text-[14px] font-semibold ring-1 ring-[#1c2634] active:scale-[0.98] transition-transform">
                Send code to my phone
              </button>
            ) : (
              <>
                <input
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  placeholder="••••••"
                  className="w-full text-center tracking-[0.6em] text-lg font-bold bg-[#0a0f18] rounded-lg px-3 py-2.5 text-white placeholder:text-[#2a3547] outline-none ring-1 ring-[#1c2634] focus:ring-[#2f8bff]"
                />
                <p className="text-[11px] text-[#5c6675] mt-1.5 text-center">Enter the 6-digit code sent to your phone (demo: any 6 digits).</p>
              </>
            )}
          </div>
        </div>

        <button
          onClick={unlock}
          disabled={!canUnlock}
          className={`mt-5 w-full py-3 rounded-xl text-[15px] font-bold transition-colors ${
            canUnlock ? 'bg-[#22c55e] text-white active:scale-[0.98]' : 'bg-[#141c28] text-[#5c6675] cursor-not-allowed'
          }`}
        >
          Unlock account
        </button>
        <p className="text-center text-[10px] text-[#5c6675] mt-3">Payment &amp; identity verification are simulated for this demo.</p>
        <button onClick={resetDemo} className="text-center text-[11px] text-[#5c6675] underline mt-2">Reset safety demo</button>
      </div>
    </div>
  );
}

export function DisableScreen() {
  const { lastReason, resetDemo, violations } = useSafety();
  const [appealed, setAppealed] = useState(false);
  const permanent = violations >= 5;
  return (
    <div className="absolute inset-0 z-50 bg-[#05070c]/97 backdrop-blur-sm flex flex-col items-center justify-center px-8 text-center">
      <div className="w-20 h-20 rounded-2xl bg-[#3a1220] flex items-center justify-center mb-5">
        <ShieldX size={40} className="text-[#f43f5e]" />
      </div>
      <h2 className="font-display text-2xl font-bold text-white">Account Disabled</h2>
      <p className="text-[14px] text-[#8b95a5] mt-3 leading-relaxed max-w-xs">
        {permanent
          ? 'You reached the maximum number of violations (5).'
          : 'A serious safety violation was detected — sending inappropriate content to a minor results in immediate removal.'}
      </p>
      <p className="text-[13px] text-[#f43f5e] mt-2">{lastReason}</p>

      {appealed ? (
        <div className="mt-6 flex items-center gap-2 text-[13px] text-[#22c55e]">
          <CheckCircle2 size={16} /> Appeal submitted. Our safety team will review it.
        </div>
      ) : (
        <button onClick={() => setAppealed(true)} className="mt-6 px-6 py-2.5 rounded-xl bg-[#161d29] text-white text-[14px] font-semibold ring-1 ring-[#1c2634] active:scale-[0.98] transition-transform">
          Submit an appeal
        </button>
      )}
      <button onClick={resetDemo} className="text-[11px] text-[#5c6675] underline mt-5">Reset safety demo</button>
    </div>
  );
}

export function SafetyGate() {
  const { locked, disabled } = useSafety();
  if (disabled) return <DisableScreen />;
  if (locked) return <LockScreen />;
  return null;
}
